export function getCountryFromCode(countryCode: string, locale?: string): string | undefined {
    return new Intl.DisplayNames([locale ?? 'de'], { type: 'region' }).of(countryCode);
}

/**
 * Converts a language code to an emoji flag.
 * Maps language codes (2-letter or 3-letter) to their corresponding country codes,
 * then converts the country code to a flag emoji.
 *
 * @param languageCode - Language code (e.g., 'en', 'eng', 'de', 'deu')
 * @returns Emoji flag (e.g., '🇬🇧', '🇩🇪') or the original language code if no mapping exists
 *
 * @example
 * getLanguageFlag('de') // returns '🇩🇪'
 * getLanguageFlag('eng') // returns '🇬🇧'
 * getLanguageFlag('fr') // returns '🇫🇷'
 */
export function getLanguageFlag(languageCode: string): string {
    const languageToCountry: Record<string, string> = {
        en: 'GB',
        eng: 'GB',
        de: 'DE',
        deu: 'DE',
        ger: 'DE',
        fr: 'FR',
        fre: 'FR',
        fra: 'FR',
        es: 'ES',
        spa: 'ES',
        it: 'IT',
        ita: 'IT',
        pt: 'PT',
        por: 'PT',
        ru: 'RU',
        rus: 'RU',
        ja: 'JP',
        jpn: 'JP',
        zh: 'CN',
        chi: 'CN',
        ko: 'KR',
        kor: 'KR',
        ar: 'SA',
        ara: 'SA',
        tr: 'TR',
        tur: 'TR',
        pl: 'PL',
        pol: 'PL',
        nl: 'NL',
        nld: 'NL',
        dut: 'NL',
        sv: 'SE',
        swe: 'SE',
        da: 'DK',
        dan: 'DK',
        no: 'NO',
        nor: 'NO',
        fi: 'FI',
        fin: 'FI',
        cs: 'CZ',
        ces: 'CZ',
        cze: 'CZ'
    };

    const countryCode = languageToCountry[languageCode?.toLowerCase()] || languageCode?.toUpperCase();
    if (!countryCode || countryCode.length !== 2) return languageCode;

    // Convert country code to flag emoji
    const codePoints = countryCode
        .toUpperCase()
        .split('')
        .map((char) => 127397 + char.charCodeAt(0));
    return String.fromCodePoint(...codePoints);
}
